import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import styles from './ProfilePage.module.css';
import O_HeaderResponsive from '@/components/O_Organisms/O_Header/O_HeaderResponsive';
import M_ProfileMenu from '@/components/M_Molecules/M_ProfileMenu/M_ProfileMenu';
import W_ProfileTab, {
  ProfileTabHandle,
} from '@/components/W_Wrappers/W_ProfileTabs/W_ProfileTab/W_ProfileTab';
import {
  W_SecurityTab,
  SecurityTabHandle,
} from '@/components/W_Wrappers/W_ProfileTabs/W_SecurityTab/W_SecurityTab';
import { W_PrivacyTab } from '@/components/W_Wrappers/W_ProfileTabs/W_PrivacyTab/W_PrivacyTab';
import { W_NotificatonsTab } from '@/components/W_Wrappers/W_ProfileTabs/W_NotificatonsTab/W_NotificatonsTab';
import { W_BillingTab } from '@/components/W_Wrappers/W_ProfileTabs/W_BillingTab/W_BillingTab';
import { W_WalletTab } from '@/components/W_Wrappers/W_ProfileTabs/W_WalletTab/W_WalletTab';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { useIsMobile } from '@/hooks/useIsMobile';
import M_ProfileMobileTabs from '@/components/M_Molecules/M_ProfileMobileTabs/M_ProfileMobileTabs';

interface ProfilePageProps {
  user: User;
  onLogout: () => void;
  onBack: () => void;
}

type ProfileTab =
  | 'profile'
  | 'security'
  | 'privacy'
  | 'notifications'
  | 'billing'
  | 'wallet';

export const ProfilePage: React.FC<ProfilePageProps> = ({
  user: initialUser,
  onLogout,
  onBack,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const { getCurrentUser, user: authUser } = useAuth();
  const user = authUser || initialUser;
  const [isProfileDirty, setIsProfileDirty] = useState(false);
  const [isSecurityDirty, setIsSecurityDirty] = useState(false);
  const profileTabRef = useRef<ProfileTabHandle | null>(null);
  const securityTabRef = useRef<SecurityTabHandle | null>(null);
  const { language } = useLanguage();
  const isMobileHeader = useIsMobile(968);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (
      tabParam &&
      [
        'profile',
        'security',
        'privacy',
        'notifications',
        'billing',
        'wallet',
      ].includes(tabParam)
    ) {
      setActiveTab(tabParam as ProfileTab);
    }
  }, [searchParams]);

  const handleTabChange = (tab: ProfileTab | string) => {
    const tabValue = tab as ProfileTab;
    setActiveTab(tabValue);
    setSearchParams({ tab: tabValue });
  };

  useEffect(() => {
    if (activeTab !== 'profile') {
      setIsProfileDirty(false);
    }
    if (activeTab !== 'security') {
      setIsSecurityDirty(false);
    }
  }, [activeTab]);

  const handleReset = () => {
    if (activeTab === 'profile') {
      profileTabRef.current?.resetChanges();
    } else if (activeTab === 'security') {
      securityTabRef.current?.resetChanges();
    }
  };

  const handleSave = async () => {
    if (activeTab === 'profile') {
      await profileTabRef.current?.saveChanges();
    } else if (activeTab === 'security') {
      await securityTabRef.current?.saveChanges();
    }

    await getCurrentUser();
  };

  const isActionBarVisible =
    (activeTab === 'profile' && isProfileDirty) ||
    (activeTab === 'security' && isSecurityDirty);

  let headerActions: React.ReactNode | null = null;

  if (isActionBarVisible) {
    if (isMobileHeader) {
      headerActions = (
        <>
          <A_CircleButton
            variant='secondary'
            icon={<Q_Icon.CrossIcon width={24} height={24} fill='none' />}
            onClick={handleReset}
            aria-label={getTranslation(language, 'profileTab.cancelChanges')}
          />
          <A_CircleButton
            variant='primary'
            icon={<Q_Icon.CheckmarkIcon width={20} height={20} fill='none' />}
            onClick={handleSave}
            aria-label={getTranslation(language, 'common.save')}
          />
        </>
      );
    } else {
      headerActions = (
        <>
          <A_Button
            variant='secondary'
            size='small'
            fontSize='text-p-4'
            onClick={handleReset}
          >
            {getTranslation(language, 'profileTab.cancelChanges')}
          </A_Button>
          <A_Button
            variant='primary'
            size='small'
            fontSize='text-p-4'
            onClick={handleSave}
          >
            {getTranslation(language, 'common.save')}
          </A_Button>
        </>
      );
    }
  }

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <W_ProfileTab
            ref={profileTabRef}
            user={user}
            onDirtyChange={setIsProfileDirty}
          />
        );
      case 'security':
        return (
          <W_SecurityTab
            ref={securityTabRef}
            user={user}
            onDirtyChange={setIsSecurityDirty}
          />
        );
      case 'privacy':
        return <W_PrivacyTab user={user} />;
      case 'notifications':
        return <W_NotificatonsTab user={user} />;
      case 'billing':
        return (
          <W_BillingTab
            hasSubscription={user.security.hasSubscription || false}
          />
        );
      case 'wallet':
        return <W_WalletTab user={user} />;
      default:
        return (
          <W_ProfileTab
            ref={profileTabRef}
            user={user}
            onDirtyChange={setIsProfileDirty}
          />
        );
    }
  };

  return (
    <div className={styles.page}>
      <O_HeaderResponsive
        showSearchInput={false}
        showCreateButton={!isActionBarVisible}
        hideRightNav={isActionBarVisible}
        actionSlot={headerActions}
      />

      <main className={styles.content}>
        <div className={styles.left_column}>
          <M_ProfileMenu
            user={user}
            onTabChange={handleTabChange}
            activeTab={activeTab}
          />
        </div>

        <div className={styles.center_column}>{renderActiveTab()}</div>
      </main>

      <M_ProfileMobileTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onLogout={onLogout}
      />
    </div>
  );
};
